import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import { type Observable, of, switchMap, tap } from 'rxjs'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { Permissions } from '@/features/iam/permissions/Permissions'
import { type TeamMember } from '@/features/iam/teams/TeamMember'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { isRefreshDesign } from '@/infra/design'
import { externalLinks } from '@/utils/link'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { type CreateJobDialogViewModel } from '@/features/jobs/drafting/components/CreateJobDialog/CreateJobDialogViewModel'
import { type SureprepStore } from '@/features/jobs/details/sureprep/SureprepStore'
import { saveFile } from '@/utils/file-util'
import { asAtom, toStream } from '@/utils/mobx-util'
import { countdown } from '@/utils/rx-util'
import { dateFormatters } from '@/utils/date-util'
import { type JobCollaboratorsViewModel } from '@/features/jobs/details/JobCollaboratorsViewModel'
import { type JobBillingViewModel } from '@/features/jobs/details/JobBillingViewModel'
import { type JobScopeChangesViewModel } from '@/features/jobs/details/JobScopeChangesViewModel'
import { type JobBookkeepingViewModel } from '@/features/jobs/details/JobBookkeepingViewModel'
import { type RatingsViewModel } from '@/features/jobs/details/ratings/RatingsViewModel'
import { type JobDocumentsViewModel } from '@/features/jobs/details/JobDocumentsViewModel'
import { type UpdateProgressionStepDialogViewModel } from '@/features/jobs/details/components/UpdateProgressionStepDialog/UpdateProgressionStepDialogViewModel'
import { type RenameJobDialogViewModel } from '@/features/jobs/RenameJobDialog/RenameJobDialogViewModel'
import { type ForfeitDialogViewModel } from '@/features/jobs/details/components/ForfeitDialog/ForfeitDialogViewModel'
import { type CorrectionsDialogViewModel } from '@/features/jobs/details/components/CorrectionsDialog/CorrectionsDialogViewModel'
import { type CreateAmendmentDialogViewModel } from '@/features/jobs/details/components/AmendmentsCard/CreateAmendmentDialog/CreateAmendmentDialogViewModel'
import { type CopyJobDialogViewModel } from '@/features/jobs/CopyJobDialog/CopyJobDialogViewModel'
import { type CloseJobAsAdminDialogViewModel } from '@/features/jobs/details/components/CloseJobAsAdminDialog/CloseJobAsAdminDialogViewModel'
import { type DeleteJobDialogViewModel } from '@/features/jobs/details/components/DeleteJobDialog/DeleteJobDialogViewModel'
import { type NotifyProsOfOpenJobDialogViewModel } from '@/features/jobs/details/components/NotifyProsOfOpenJobDialog/NotifyProsOfOpenJobDialogViewModel'
import { type SubmitJobDialogViewModel } from '@/features/jobs/details/components/SubmitJobDialog/SubmitJobDialogViewModel'
import { type UpdateDeadlineDialogViewModel } from '@/features/jobs/details/components/UpdateDeadlineDialog/UpdateDeadlineDialogViewModel'
import { type ReopenJobAsAdminDialogViewModel } from '@/features/jobs/details/components/ReopenJobAsAdminDialog/ReopenJobAsAdminDialogViewModel'

export interface JobMenuAction {
  title: string
  /**
   * A second line under the title.
   */
  description?: string
  isDisabled?: boolean
  testId?: string
  onSelect: () => unknown
}

export interface JobMenuSection {
  /**
   * Shown under a divider. The first section has none.
   */
  title?: string
  actions: JobMenuAction[]
}

/**
 * State the actions menu reads from the job view model.
 */
export interface JobActionsContext {
  readonly isPro: boolean
  readonly showDraftView: boolean
  readonly questionsCompleted: boolean
  readonly canViewDocs: boolean
  readonly canOverrideJobProgressionStep: boolean
  readonly teamOrMemberPermissions: readonly Permissions[]
  readonly currentTeamMemberOfOwnerTeam: TeamMember | undefined
}

/**
 * The actions on a job that its actions menu offers, and their dialogs.
 */
export class JobActionsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Ticks every second in sync with clock time until the advance date is
   * reached. `null` when the job has no advance date.
   */
  private readonly _secondsUntilAdvanceAllowed = asAtom(
    toStream(() => this.advanceAllowedDate, true).pipe(
      switchMap((date) => (date ? countdown(date) : of(null))),
    ),
    null,
  )

  constructor(
    private readonly _job: Job,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getContext: () => JobActionsContext,
    private readonly _jobStore: JobStore,
    private readonly _sureprepStore: SureprepStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _renameShortcut$: Observable<unknown>,
    private readonly _jobCreationDialogVm: CreateJobDialogViewModel,
    private readonly _collaboratorsVm: JobCollaboratorsViewModel,
    private readonly _billingVm: JobBillingViewModel,
    private readonly _scopeChangesVm: JobScopeChangesViewModel,
    private readonly _bookkeepingVm: JobBookkeepingViewModel,
    private readonly _ratingsVm: RatingsViewModel,
    private readonly _documentsVm: JobDocumentsViewModel,
    private readonly _updateProgressionStepDialogVm: UpdateProgressionStepDialogViewModel,
    public readonly renameVm: RenameJobDialogViewModel,
    public readonly forfeitVm: ForfeitDialogViewModel,
    public readonly correctionsVm: CorrectionsDialogViewModel,
    public readonly createAmendmentVm: CreateAmendmentDialogViewModel,
    public readonly copyVm: CopyJobDialogViewModel,
    public readonly closeJobAsAdminDialogVm: CloseJobAsAdminDialogViewModel,
    public readonly deleteJobDialogVm: DeleteJobDialogViewModel,
    public readonly notifyProsOfOpenJobDialogVm: NotifyProsOfOpenJobDialogViewModel,
    public readonly submitJobDialogVm: SubmitJobDialogViewModel,
    public readonly updateDeadlineDialogVm: UpdateDeadlineDialogViewModel,
    public readonly reopenJobAsAdminVm: ReopenJobAsAdminDialogViewModel,
  ) {
    this._lifecycle.manage(this.closeJobAsAdminDialogVm)
    this._lifecycle.manage(this.createAmendmentVm)
    this._lifecycle.manage(this.correctionsVm)
    this._lifecycle.manage(this._renameShortcut$.pipe(tap(this.renameVm.show)))
  }

  /**
   * Whether we can copy the job.
   */
  @computed
  get canCopy() {
    return (
      (this._getContext().teamOrMemberPermissions.some(
        (p) => p.startNewJob || p.createTeamJobs,
      ) ||
        this._permissions.adminJobCreate) &&
      !this._job.isDraft
    )
  }

  /**
   * Determines whether we can rename the job.
   */
  @computed
  get canRename() {
    const permissions = this._getContext().teamOrMemberPermissions
    const collaborator = this._job.currentUserCollaborator
    if (collaborator?.type === 'Pro') {
      return (
        this._job.status !== 'Closed' &&
        permissions.some((p) => p.jobProviderUpdateName)
      )
    }

    return (
      this._job.status !== 'Closed' &&
      (permissions.some((p) => p.jobUpdate) ||
        (collaborator ? collaborator.role === 'Champion' : false))
    )
  }

  /**
   * Determines whether we can view amendments for the job.
   */
  @computed
  get canViewAmendments() {
    return Boolean(this._permissions.adminJobAmend && !this._job.isDraft)
  }

  @computed
  get hasVisibleAmendments() {
    return this.canViewAmendments && this._job.amendments.length > 0
  }

  /**
   * Whether we can reopen the job.
   */
  @computed
  get canReopenAsAdmin() {
    return this._job.completed && this._permissions.adminJobReopen
  }

  /**
   * Whether we are able to close the job as a collaborator.
   */
  @computed
  get canCloseAsCollaborator() {
    const isClient = this._job.currentUserCollaborator?.type === 'Client'

    return (
      this._job.currentUserCollaborator &&
      !isClient &&
      this._getContext().teamOrMemberPermissions.some((p) => p.jobClose) &&
      this._job.status !== 'Closed'
    )
  }

  /**
   * Whether we are able to close the job as an admin.
   */
  @computed
  get canCloseAsAdmin() {
    return (
      this._permissions.adminJobClose &&
      !this._job.isDraft &&
      !this._job.completed
    )
  }

  /**
   * Whether we can delete the job as an admin.
   */
  @computed
  get canDeleteJobAsAdmin() {
    return this._permissions.adminJobDelete
  }

  /**
   * Whether we can delete the draft as its client.
   */
  @computed
  get canDeleteJobAsCollaborator() {
    return (
      this._job.isDraft &&
      (this._job.isChampionClientCollaborator ||
        Boolean(this._getContext().currentTeamMemberOfOwnerTeam))
    )
  }

  @computed
  get canDelete() {
    return this.canDeleteJobAsAdmin || this.canDeleteJobAsCollaborator
  }

  /**
   * Whether we are able to download the binder.
   */
  @computed
  get canDownloadBinder() {
    return this._job.canDownloadBinder
  }

  /**
   * Whether we are able to make corrections.
   */
  @computed
  get canMakeCorrections() {
    if (this._job.status === 'Closed' || this._job.isPending) {
      return false
    }

    if (this._permissions.adminJobUpdate) {
      return true
    }

    return this._getContext().teamOrMemberPermissions.some(
      (p) => p.jobUpdate || p.updateTeamJobs,
    )
  }

  /**
   * Indicates whether the actor can notify pros of the available job.
   */
  @computed
  get canNotifyPros() {
    return this._permissions.adminJobUpdate && this._job.status === 'Unclaimed'
  }

  /**
   * Whether we are able to update the job deadline.
   */
  @computed
  get canUpdateDeadline() {
    return this._job.status !== 'Closed' && this._permissions.adminJobUpdate
  }

  /**
   * Whether forfeiting is enabled.
   */
  @computed
  get canForfeit() {
    const collaborator = this._job.currentUserCollaborator
    if (!collaborator) {
      return false
    }

    return (
      this._job.status !== 'Closed' &&
      (collaborator.type === 'Pro' || collaborator.role === 'Supporting') &&
      this._getContext().teamOrMemberPermissions.some((p) => p.jobForfeit)
    )
  }

  @computed
  get canPutJobOnHold() {
    return this._job.isClaimedJob && this._canHoldAndResumeJob
  }

  @computed
  get canResumeJob() {
    return this._job.isOnHold && this._canHoldAndResumeJob
  }

  @computed
  get canAdvanceJobAsAdmin() {
    return this._job.isPending && this._permissions.adminJobUpdate
  }

  /**
   * The date from which the pending job is allowed to advance, if it has one.
   */
  @computed
  get advanceAllowedDate(): Date | null {
    return this._job.isPending ? this._job.dateAdvanceAllowed : null
  }

  /**
   * Whether advancing the job now would be ahead of its advance date.
   */
  @computed
  get isAdvancingEarly() {
    return (this._secondsUntilAdvanceAllowed() ?? 0) > 0
  }

  /**
   * A champion forfeits the job; anyone else leaves it.
   */
  @computed
  get forfeitTitle() {
    const role =
      this._collaboratorsVm.currentUserCollaborator?.role || 'Supporting'
    return role === 'Champion' ? 'Forfeit job' : 'Leave'
  }

  /**
   * The actions menu, in the order it lists them.
   */
  @computed
  get menuSections(): JobMenuSection[] {
    const collaborators = this._collaboratorsVm
    const billing = this._billingVm
    const scopeChanges = this._scopeChangesVm
    const bookkeeping = this._bookkeepingVm
    const context = this._getContext()

    const actions: MaybeAction[] = [
      billing.canAddCouponAsAdmin && {
        title: 'Add coupon',
        testId: 'add-coupon-dropdown-item',
        onSelect: billing.applyCouponAsAdminVm.show,
      },
      billing.canAddManualInvoice && {
        title: 'Add manual invoice',
        testId: 'add-manual-invoice-dropdown-item',
        onSelect: billing.addManualInvoiceDialogVm.modal.show,
      },
      this.canViewAmendments && {
        title: 'Add new amendment',
        testId: 'add-new-amendment-dropdown-item',
        onSelect: this.createAmendmentVm.show,
      },
      collaborators.canAddCollaboratorsAsAdmin && {
        title: 'Add collaborator',
        testId: 'add-pro-dropdown-item',
        onSelect: collaborators.addCollaboratorsAsAdminVm.show,
      },
      billing.canChargeOrRefundJob && {
        title: 'Charge/refund job',
        testId: 'charge-or-refund-job-dropdown-item',
        onSelect: billing.chargeOrRefundDialogVm.show,
      },
      bookkeeping.canGrantBookkeepingAccessAsAdmin && {
        title: 'Grant bookkeeping access',
        testId: 'grant-bookkeeping-access-dropdown-item',
        onSelect: bookkeeping.bookkeepingGrantAccessDialogVm.show,
      },
      bookkeeping.canCorrectBookkeepingCycles && {
        title: 'Correct cycle',
        testId: 'correct-cycle-dropdown-item',
        onSelect: bookkeeping.showSelectCycleDialog,
      },
      this.canCopy && {
        title: 'Clone job',
        testId: 'copy-dropdown-item',
        onSelect: this.copyVm.modal.show,
      },
      this.canCloseAsAdmin && {
        title: 'Complete job as admin',
        testId: 'complete-job-as-admin-item',
        onSelect: this.requestCloseAsAdmin,
      },
      this.canDelete && {
        title: 'Delete job',
        testId: 'delete-job-dropdown-item',
        onSelect: this.deleteJobDialogVm.modal.show,
      },
      // The classic header's menu never offered this.
      isRefreshDesign &&
        context.canViewDocs &&
        this._documentsVm.documents.length > 0 && {
          title: 'Download all documents',
          testId: 'download-all-documents-dropdown-item',
          onSelect: this._documentsVm.downloadAll,
        },
      this.canDownloadBinder && {
        title: 'Download PBFX',
        onSelect: this.downloadBinder,
      },
      context.showDraftView && {
        title: 'Edit responses',
        testId: 'edit-responses-dropdown-item',
        onSelect: this.editDraft,
      },
      this.canMakeCorrections &&
        !context.showDraftView && {
          title: 'Make corrections',
          testId: 'make-corrections-dropdown-item',
          onSelect: this.correctionsVm.show,
        },
      this.canNotifyPros && {
        title: 'Notify pros',
        testId: 'notify-pros-dropdown-item',
        onSelect: this.notifyProsOfOpenJobDialogVm.modal.show,
      },
      context.isPro && {
        title: 'Pro support',
        testId: 'job-details-pro-support-dropdown-item',
        onSelect: this.openProSupport,
      },
      this._ratingsVm.canRatePro && {
        title: 'Rate Pro',
        testId: 'rate-pro-dropdown-item',
        onSelect: this._ratingsVm.showRateProDialog,
      },
      collaborators.canRemoveClientTeamAsAdmin && {
        title: 'Remove client team',
        testId: 'remove-client-team-dropdown-item',
        onSelect: collaborators.removeClientTeamVm.show,
      },
      collaborators.canRemoveProsAsAdmin && {
        title: 'Remove collaborator',
        testId: 'remove-pro-dropdown-item',
        onSelect: collaborators.removeCollaboratorsAsAdminVm.show,
      },
      this.canRename && {
        title: 'Rename job',
        onSelect: this.renameVm.show,
      },
      this.canReopenAsAdmin && {
        title: 'Reopen job',
        testId: 'reopen-job-dropdown-item',
        onSelect: this.reopenJobAsAdminVm.show,
      },
      scopeChanges.canRequestScopeChanges && {
        title: 'Request scope changes',
        testId: 'request-scope-changes-dropdown-item',
        onSelect: scopeChanges.requestScopeChangesVm.show,
      },
      billing.canSplitPayout && {
        title: 'Split payout',
        testId: 'split-payout-dropdown-item',
        onSelect: billing.splitPayoutDialogVm.show,
      },
      collaborators.canTransferJobAsAdmin && {
        title: 'Transfer job',
        testId: 'transfer-job-dropdown-item',
        onSelect: collaborators.transferJob,
      },
      this.canUpdateDeadline && {
        title: 'Update deadline',
        testId: 'update-deadline-dropdown-item',
        onSelect: this.updateDeadlineDialogVm.modal.show,
      },
      context.canOverrideJobProgressionStep && {
        title: 'Update job progression',
        testId: 'update-job-progression-dropdown-item',
        onSelect: this._updateProgressionStepDialogVm.show,
      },
      scopeChanges.canUpdateJobScope && {
        title: 'Update job scope',
        testId: 'update-job-scope-dropdown-item',
        onSelect: scopeChanges.updateJobScopeVm.show,
      },
    ]

    const statusActions: MaybeAction[] = [
      this.canForfeit && {
        title: this.forfeitTitle,
        onSelect: this.forfeitVm.modal.show,
      },
      this.canPutJobOnHold && {
        title: 'Place job on hold',
        onSelect: this.placeJobOnHold,
      },
      this.canResumeJob && {
        title: 'Resume job',
        onSelect: this.resumeJob,
      },
      this.canAdvanceJobAsAdmin && {
        title: 'Advance job',
        description:
          this.isAdvancingEarly && this.advanceAllowedDate
            ? `Advancing early. Allowed from ${dateFormatters.dateTimeDefault.format(this.advanceAllowedDate)}`
            : undefined,
        isDisabled: this.advanceJob.pending,
        testId: 'advance-job-dropdown-item',
        onSelect: this.advanceJob,
      },
    ]

    const submitActions: MaybeAction[] = [
      context.showDraftView &&
        context.questionsCompleted && {
          title: 'Submit job',
          testId: 'submit-job-dropdown-item',
          onSelect: this.submitJobDialogVm.show,
        },
    ]

    const sections: JobMenuSection[] = [
      { actions: actions.filter(isAction) },
      { title: 'Set job status', actions: statusActions.filter(isAction) },
      { title: 'Status', actions: submitActions.filter(isAction) },
    ]
    return sections.filter((section) => section.actions.length > 0)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
    this.renameVm.modal.close()
    this.forfeitVm.modal.close()
    this.correctionsVm.modal.close()
    this.copyVm.modal.close()
  }

  /**
   * Opens job creation dialog.
   */
  @action.bound
  editDraft() {
    void this._jobCreationDialogVm.open([this._job.id])
  }

  /**
   * Requests to show the close dialog for use by admins.
   */
  @action.bound
  requestCloseAsAdmin() {
    if (this.canCloseAsAdmin) {
      this.closeJobAsAdminDialogVm.modal.show()
    }
  }

  @action.bound
  openProSupport() {
    window.open(externalLinks.proSupportTicket())
  }

  @action.bound
  async placeJobOnHold() {
    const alert = this._newAlert()
      .withState('pending')
      .saying('Placing this job on hold', 'hang tight!')
      .show()
    try {
      await this._jobStore.placeJobOnHold(this._job.id)
      alert
        .withState('success')
        .saying('Success!', 'Job has been placed on hold!')
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Placing the job on hold failed')(err)
    }
  }

  /**
   * Resume a job that is on hold
   */
  @action.bound
  async resumeJob() {
    const alert = this._newAlert()
      .withState('pending')
      .saying('Resuming this job', 'Hang tight!')
      .show()
    try {
      await this._jobStore.resumeJob(this._job.id)
      alert.withState('success').saying('Success!', 'Job has been resumed!')
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Resuming the job failed')(err)
    }
  }

  /**
   * Advances a job that is pending.
   */
  advanceJob = task.resolved(async () => {
    const alert = this._newAlert()
      .withState('pending')
      .saying('Advancing this job', 'Hang tight!')
      .show()
    try {
      await this._jobStore.advanceJob(this._job.id)
      alert.withState('success').saying('Success!', 'Job has been advanced!')
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Advancing the job failed')(err)
    }
  })

  /**
   * Downloads binder for job
   */
  @action.bound
  async downloadBinder() {
    const alert = this._newAlert()
      .withState('pending')
      .saying('Getting the Binder from Sureprep', 'Hang tight!')
      .show()

    try {
      const binder = await this._sureprepStore.fetchBinder(this._job.id)
      saveFile(binder, `binder-${this._job.id}.pbfx`)
      alert
        .withState('success')
        .saying(
          `Binder has been received!`,
          `Your download will begin shortly.`,
        )
        .autoDismiss()
        .show()
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Getting the Sureprep binder failed')(err)
    }
  }

  /**
   * A collaborator needs the workspace permission; anyone else on the job's
   * teams needs it at both the workspace and the team level.
   */
  @computed
  private get _canHoldAndResumeJob() {
    const workspacePermission = this._permissions.jobOnHold
    if (this._job.currentUserCollaborator && workspacePermission) {
      return true
    }

    const teamPermission = this._getContext().teamOrMemberPermissions.some(
      (p) => p.jobOnHold,
    )
    if (workspacePermission && teamPermission) {
      return true
    }

    return this._permissions.adminJobOnHold
  }

  @computed
  private get _permissions() {
    return this._getCurrentMember(this._job.workspace.id).permissions
  }
}

type MaybeAction = JobMenuAction | false | null | undefined

function isAction(action: MaybeAction): action is JobMenuAction {
  return Boolean(action)
}
